/************************************************************************
 ** IBM Confidential
 **
 ** IBM Business Analytics: Rapidly Adaptive Visualization Engine
 **
 ** (C) Copyright IBM Corp. 2020
 **
 ** The source code for this program is not published or otherwise divested of its trade secrets, 
 ** irrespective of what has been deposited with the U.S. Copyright Office.
 ************************************************************************/
// GENERATED
import BaseMoserObject from './BaseMoserObject';
import ExpressionUtil from './ExpressionUtil';
import HighLevelDataType from './HighLevelDataType';
import MacroUtil from './MacroUtil';
import FormatSymbols from './FormatSymbols';
import BinExpressionUtil from './BinExpressionUtil';
//@import BaseMoserObject (loadtime) // superclass
//@import com/ibm/bi/platform/moser/expressions/v5/util/ExpressionUtil (static) // elseExp, cast, getLiteral, assembleCase, whenThen, isNull, ceil, nest, minimumForReport, maximumForReport, floor, inlineIfThenElse
//@import com/ibm/bi/platform/moser/common/generated/metadata/HighLevelDataType (runtime) // HighLevelDataType
//@import com/ibm/bi/platform/moser/expressions/v5/util/MacroUtil (static) // macro, macroAsString, getLiteral, injectMacro, getResourceString, substr, substitute
//@import FormatSymbols (runtime) // getListSeparator
//@import com/ibm/bi/platform/moser/expressions/v5/util/BinExpressionUtil (runtime) // formatDouble, widthBucket

/**
 * Class to build binning V5 expressions, as well as values needed to populate the MUI binning wizard.
 * @author  PASCALLacombe
 */
export default class BinExpression extends BaseMoserObject{

	querySubject = null;

	queryItem = null;

	identifier = null;

	numberOfBins = 0;

	maxDecimalPlaces = 0;

	minValue = 0;

	maxValue = 0;

	customLabels = null;

	customRanges = null;

	defaultLabels = null;

	generateNullBin = false;

	expressionLocale = null;

	minForReport = null;

	maxForReport = null;

	binWidth = null;

	integerCheck = null;

	spillOver = null;

	virtualMinValue = null;

	virtualMaxValue = null;



	/**
	 * Constructor
	 * @param {QuerySubject} querySubject  - Optional QuerySubject to build identifiers against, when null single-part identifiers are generated
	 * @param {QueryItem} queryItem  - QueryItem to bin
	 * @param {int} numberOfBins  - The number of bins to create
	 * @param {int} maxDecimalPlaces  - For QueryItem with DECIMAL HighLevelDatatype only, specifies the maximum number of digits after the decimal place.
	 * @param {double} minValue  - The minimum value for report
	 * @param {double} maxValue  - The maximum value f
	 * @param {Array} customLabels  - Optional list of strings representing the custom labels, when non-null the size is expected to be equal to (numberOfBins + 1) and when a value is null the default label will be used customLabels[0] is the label for null customLabels[1] is the label for bucket #1 customLabels[N] is the label for bucket #N
	 * @param {Array} customRange  - Optional list of strings (numeric) representing the custom ranges, when non-null the size is expected to be equal to numberOfBins and all ranges need to be populated except the first and last values, which when null means those bins are not bound to a value
	 * @param {Array} defaultLabels  - The default bin labels, which are the resolved macros from getLabelMacros() in order of : Null, Low, Middle, High
	 * @param {boolean} generateNullBin  - Whether we should create the null bin or not
	 * @param {String} expressionLocale  - Optional string representation of locale. This locale will determine list separator used in expressions
	 */
	constructor(querySubject, queryItem, numberOfBins, maxDecimalPlaces, minValue, maxValue, customLabels, customRange, defaultLabels, generateNullBin, expressionLocale) {
		super();
		this.querySubject = querySubject;
		this.queryItem = queryItem;
		this.identifier = !querySubject ? queryItem.getIdentifier() : querySubject.getIdentifier() + "." + queryItem.getIdentifier();
		this.numberOfBins = numberOfBins;
		this.maxDecimalPlaces = maxDecimalPlaces;
		this.minValue = minValue;
		this.maxValue = maxValue;
		this.customLabels = customLabels;
		this.customRanges = customRange;
		this.defaultLabels = defaultLabels;
		this.generateNullBin = generateNullBin;
		this.expressionLocale = expressionLocale;
		this.minForReport = ExpressionUtil.minimumForReport(this.identifier);
		this.maxForReport = ExpressionUtil.maximumForReport(this.identifier);
		this._init();
	}

	/**
	 * Initialize common V5 expressions
	 */
	_init() {
		this.integerCheck = this._integerCheck();
		this.binWidth = this._binWidth();
		this.spillOver = this._spillOver();
		this.virtualMinValue = this._virtualMinValue();
		this.virtualMaxValue = this._virtualMaxValue();
	}

	/**
	 * Creates a V5 expression that will check whether an integer range is less than the number of bin requested
	 * @return {String}
	 */
	_integerCheck() {
		var condition = this.maxForReport;
		condition+=(" - ");
		condition+=(this.minForReport);
		condition+=(" < ");
		condition+=(this.numberOfBins);
		return condition;
	}

	/**
	 * Creates a expression that will return the width of a automatic bin.
	 * @return {String}
	 */
	_binWidth() {
		var result = "( ";
		result+=(this.maxForReport);
		result+=(" - ");
		result+=(this.minForReport);
		result+=(" ) / ");
		result+=(this.numberOfBins);
		return result;
	}

	/**
	 * Create a V5 expression that calculates the new minimum when requesting a number of bins that is higher than the integer range.
	 * @return {String}
	 */
	_virtualMinValue() {
		var expression = this.minForReport;
		expression+=(" - ");
		expression+=(ExpressionUtil.floor(this.spillOver));
		return expression;
	}

	/**
	 * Create a V5 expression that calculates the new maximum when requesting a number of bins that is higher than the integer range.
	 * @return {String}
	 */
	_virtualMaxValue() {
		var expression = this.maxForReport;
		expression+=(" + ");
		expression+=(ExpressionUtil.ceil(this.spillOver));
		return expression;
	}

	/**
	 * Create a V5 expression that calculates the bin spillover.
	 * @return {String}
	 */
	_spillOver() {
		var expression = "( ";
		expression+=(this.numberOfBins);
		expression+=(" - ");
		expression+=(this.maxForReport);
		expression+=(" + ");
		expression+=(this.minForReport);
		expression+=(" ) / 2");
		return expression;
	}

	/**
	 * Update the number of bins, this resets both the custom labels and custom ranges.
	 * @param {int} value  - New number of bins
	 */
	updateNumberOfBins(value) {
		this.numberOfBins = value;
		this.customLabels = null;
		this.customRanges = null;
		this._init();
	}

	/**
	 * Update the maximum number of decimal places, only honored when HighLevelDatatype of the QueryItem is DECIMAL
	 * @param {int} value
	 */
	updateMaxDecimalPlaces(value) {
		this.maxDecimalPlaces = value;
	}

	/**
	 * Update the custom labels, expects a full list of new labels.
	 * @param {Array} labels  - New custom label strings
	 */
	updateCustomLabels(labels) {
		this.customLabels = labels;
	}

	/**
	 * Update the custom values
	 * @param {Array} ranges  - New custom range strings (numeric)
	 */
	updateCustomRanges(ranges) {
		this.customRanges = ranges;
	}

	/**
	 * Update the generate null bin value
	 * @param {boolean} value  - Whether to generate null bin
	 */
	updateGenerateNullBin(value) {
		this.generateNullBin = value;
	}

	/**
	 * Returns a V5 expression to be used in the bin number QueryItem. For examples see: getBinNumberExpressionInteger(), getBinNumberExpressionDecimal() and getBinNumberExpressionCustomRanges()
	 * @return {String}  V5 expression to calculate a bin number
	 */
	getBinNumberExpression() {
		if (this._isCustomRanges()) {
			return this._getBinNumberExpressionCustomRanges();
		}
		if (HighLevelDataType.INTEGER === this.queryItem.getHighlevelDatatype()) {
			return this._getBinNumberExpressionInteger();
		}
		return this._getBinNumberExpressionDecimal();
	}

	/**
	 * Check whether custom ranges have been supplied.
	 * @return {boolean}
	 */
	_isCustomRanges() {
		if (!this.customRanges) {
			return false;
		}
		for (var __i_enFor0 = 0, __exp_enFor0 = this.customRanges, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var customRange = __exp_enFor0[__i_enFor0];
			if (customRange != null) {
				return true;
			}
		}
		return false;
	}

	/**
	 * Return a V5 expression to be used in the bin label QueryItem.
	 * @param {String} id  - The bin number queryItem identifier
	 * @return {String}
	 */
	getLabelExpression(id) {
		if (this._isCustomRanges()) {
			return this._getLabelCustomRanges(id);
		}
		var numExtraCustomLabels = this._countExtraCustomLabels();
		var expressions = [];
		expressions.push(this._whenThenBinLow(id));
		if (numExtraCustomLabels > 0) {
			this._whenThenCustomBins(id, expressions);
		}
		if (numExtraCustomLabels < this.numberOfBins - 2) {
			expressions.push(this._whenThenBinMiddle(id));
		}
		expressions.push(this._whenThenBinHigh(id));
		expressions.push(this._whenThenBinEdge(id));
		if (this.generateNullBin) {
			var nullLabel = this.customLabels && this.customLabels[0] != null ? this.customLabels[0] : null;
			nullLabel = nullLabel == null ? MacroUtil.macro(MacroUtil.macroAsString(BinExpression._binNull(this.expressionLocale))) : ExpressionUtil.getLiteral(nullLabel);
			expressions.push(ExpressionUtil.elseExp(nullLabel));
		}
		return ExpressionUtil.assembleCase(BinExpression._toArray(expressions));
	}

	/**
	 * Return a V5 expression to be used in the bin label QueryItem when custom ranges are specified.
	 * @param {String} binIdentifier
	 * @return {String}
	 */
	_getLabelCustomRanges(binIdentifier) {
		var expressions = [];
		expressions.push(this._whenThenBinLow(binIdentifier));
		for (var i = 2; i < this.numberOfBins; ++i) {
			expressions.push(this._whenThenBinMiddleCustomRanges(binIdentifier, i));
		}
		expressions.push(this._whenThenBinHigh(binIdentifier));
		expressions.push(this._whenThenBinEdge(binIdentifier));
		if (this.generateNullBin) {
			var nullLabel = this.customLabels && this.customLabels[0] != null ? this.customLabels[0] : null;
			nullLabel = nullLabel == null ? MacroUtil.macro(MacroUtil.macroAsString(BinExpression._binNull(this.expressionLocale))) : ExpressionUtil.getLiteral(nullLabel);
			expressions.push(ExpressionUtil.elseExp(nullLabel));
		}
		return ExpressionUtil.assembleCase(BinExpression._toArray(expressions));
	}

	/**
	 * Count the number of custom labels the user entered that are not the null, first or last bin labels.
	 * @return {int}
	 */
	_countExtraCustomLabels() {
		if (!this.customLabels) {
			return 0;
		}
		var count = 0;
		for (var i = 2; i < this.numberOfBins; ++i) {
			if (this.customLabels[i] != null) {
				++count;
			}
		}
		return count;
	}

	/**
	 * Creates the when() expressions for custom bins.
	 * @param {String} binIdentifier
	 * @param {Array} expressions
	 */
	_whenThenCustomBins(binIdentifier, expressions) {
		for (var binNumber = 2; binNumber < this.numberOfBins; ++binNumber) {
			var customLabel = this.customLabels[binNumber];
			if (customLabel == null) {
				continue;
			}
			expressions.push(BinExpression._whenThenBinCustom(binIdentifier, binNumber, customLabel));
		}
	}

	/**
	 * Creates the when() expression for a custom bin.
	 * @param {String} binIdentifier
	 * @param {int} binNumber
	 * @param {String} customLabel
	 * @return {String}
	 */
	static _whenThenBinCustom(binIdentifier, binNumber, customLabel) {
		var condition = binIdentifier;
		condition+=(" = ");
		condition+=(binNumber);
		return ExpressionUtil.whenThen(condition, ExpressionUtil.getLiteral(customLabel));
	}

	/**
	 * Creates the when() then() expression for the middle bin.
	 * @param {String} binIdentifier  - The bin identifier
	 * @return {String}
	 */
	_whenThenBinMiddle(binIdentifier) {
		var condition = binIdentifier;
		condition+=(" > 1 and ");
		condition+=(binIdentifier);
		condition+=(" < ");
		condition+=(this.numberOfBins);
		var binLow = MacroUtil.injectMacro(this._binMiddleLowCalc(binIdentifier));
		var binHigh = MacroUtil.injectMacro(this._binMiddleHighCalc(binIdentifier));
		return ExpressionUtil.whenThen(condition, this._thenBinMiddle(binLow, binHigh));
	}

	/**
	 * Creates the when() then() expression for the middle bin when custom ranges have been specified.
	 * @param {String} binIdentifier  - The bin identifier
	 * @param {int} binNumber  - The bin number
	 * @return {String}
	 */
	_whenThenBinMiddleCustomRanges(binIdentifier, binNumber) {
		var condition = binIdentifier;
		condition+=(" = ");
		condition+=(binNumber);
		var thenValue;
		if (this.customLabels && this.customLabels[binNumber] != null) {
			thenValue = ExpressionUtil.getLiteral(this.customLabels[binNumber]);
		} else {
			var binLow = this.customRanges[binNumber - 1];
			var binHigh = this.customRanges[binNumber];
			thenValue = this._thenBinMiddle(binLow, binHigh);
		}
		return ExpressionUtil.whenThen(condition, thenValue);
	}

	/**
	 * Create the expression for the middle bin.
	 * @param {String} binLow
	 * @param {String} binHigh
	 * @return {String}
	 */
	_thenBinMiddle(binLow, binHigh) {
		if (binLow == null) {
			var thenValue = MacroUtil.substitute(ExpressionUtil.getLiteral("%1"), ExpressionUtil.getLiteral(binHigh), BinExpression._binLow(this.expressionLocale), this.expressionLocale);
			thenValue = MacroUtil.macroAsString(thenValue);
			return MacroUtil.macro(thenValue);
		}
		if (binHigh == null) {
			var thenValue = MacroUtil.substitute(ExpressionUtil.getLiteral("%1"), ExpressionUtil.getLiteral(binLow), BinExpression._binHigh(this.expressionLocale), this.expressionLocale);
			thenValue = MacroUtil.macroAsString(thenValue);
			return MacroUtil.macro(thenValue);
		}
		var thenValue = MacroUtil.substitute(ExpressionUtil.getLiteral("%1"), ExpressionUtil.getLiteral(binLow), BinExpression._binMiddle(this.expressionLocale), this.expressionLocale);
		thenValue = MacroUtil.substitute(ExpressionUtil.getLiteral("%2"), ExpressionUtil.getLiteral(binHigh), thenValue, this.expressionLocale);
		thenValue = MacroUtil.macroAsString(thenValue);
		return MacroUtil.macro(thenValue);
	}

	/**
	 * Expression to calculate a middle bin's low value
	 * @param {String} binIdentifier
	 * @return {String}
	 */
	_binMiddleLowCalc(binIdentifier) {
		if (HighLevelDataType.INTEGER === this.queryItem.getHighlevelDatatype()) {
			return this._binMiddleLowCalcInteger(binIdentifier);
		}
		var bucketStart = this.minForReport;
		bucketStart+=(" + ( ");
		bucketStart+=(binIdentifier);
		bucketStart+=(" - 1 ) * ");
		bucketStart+=(this.binWidth);
		return this._castToDecimalThenVarchar$0(bucketStart);
	}

	/**
	 * Expression to calculate a middle bin's low value for an INTEGER
	 * @param {String} binIdentifier
	 * @return {String}
	 */
	_binMiddleLowCalcInteger(binIdentifier) {
		var bucketStartInteger = binIdentifier;
		bucketStartInteger+=(" + ");
		bucketStartInteger+=(this.virtualMinValue);
		bucketStartInteger+=(" - 1");
		var bucketStart = this.minForReport;
		bucketStart+=(" + ( ");
		bucketStart+=(binIdentifier);
		bucketStart+=(" - 1 ) * ");
		bucketStart+=(this.binWidth);
		return this._castToDecimalThenVarchar$0(ExpressionUtil.inlineIfThenElse(this.integerCheck, bucketStartInteger, bucketStart));
	}

	/**
	 * Expression to calculate a middle bin's high value
	 * @param {String} binIdentifier
	 * @return {String}
	 */
	_binMiddleHighCalc(binIdentifier) {
		if (HighLevelDataType.INTEGER === this.queryItem.getHighlevelDatatype()) {
			return this._binMiddleHighCalcInteger(binIdentifier);
		}
		var bucketEnd = this.minForReport;
		bucketEnd+=(" + ");
		bucketEnd+=(binIdentifier);
		bucketEnd+=(" * ");
		bucketEnd+=(this.binWidth);
		return this._castToDecimalThenVarchar$0(bucketEnd);
	}

	/**
	 * Expression to calculate a middle bin's high value for an INTEGER
	 * @param {String} binIdentifier
	 * @return {String}
	 */
	_binMiddleHighCalcInteger(binIdentifier) {
		var bucketEndInteger = binIdentifier;
		bucketEndInteger+=(" + ");
		bucketEndInteger+=(this.virtualMinValue);
		var bucketEnd = this.minForReport;
		bucketEnd+=(" + ");
		bucketEnd+=(binIdentifier);
		bucketEnd+=(" * ");
		bucketEnd+=(this.binWidth);
		return this._castToDecimalThenVarchar$0(ExpressionUtil.inlineIfThenElse(this.integerCheck, bucketEndInteger, bucketEnd));
	}

	/**
	 * Creates the when() expression for the lowest bin.
	 * @param {String} binIdentifier  - The bin identifier
	 * @return {String}
	 */
	_whenThenBinLow(binIdentifier) {
		var condition = binIdentifier;
		condition+=(" = 1");
		var thenValue;
		if (this.customLabels && this.customLabels[1] != null) {
			thenValue = ExpressionUtil.getLiteral(this.customLabels[1]);
		} else if (this._isCustomRanges()) {
			thenValue = this._thenBinMiddle(this.customRanges[0], this.customRanges[1]);
		} else {
			var binLow = MacroUtil.injectMacro(this._binLowCalc());
			thenValue = MacroUtil.substitute(ExpressionUtil.getLiteral("%1"), ExpressionUtil.getLiteral(binLow), BinExpression._binLow(this.expressionLocale), this.expressionLocale);
			thenValue = MacroUtil.macroAsString(thenValue);
			thenValue = MacroUtil.macro(thenValue);
		}
		return ExpressionUtil.whenThen(condition, thenValue);
	}

	/**
	 * Calculation that generates the number to put in the binLow label
	 * @return {String}
	 */
	_binLowCalc() {
		if (HighLevelDataType.INTEGER === this.queryItem.getHighlevelDatatype()) {
			return this._binLowCalcInteger();
		}
		var binLow = this.minForReport;
		binLow+=(" + ");
		binLow+=(this.binWidth);
		return this._castToDecimalThenVarchar$0(binLow);
	}

	/**
	 * Calculation that generates the number to put in the binLow label for an INTEGER
	 * @return {String}
	 */
	_binLowCalcInteger() {
		var binLowInteger = this.virtualMinValue;
		binLowInteger+=(" + 1");
		var binLow = this.minForReport;
		binLow+=(" + ");
		binLow+=(this.binWidth);
		return this._castToDecimalThenVarchar$0(ExpressionUtil.inlineIfThenElse(this.integerCheck, binLowInteger, binLow));
	}

	/**
	 * Creates the when() expression for the highest bin.
	 * @param {String} binIdentifier  - The bin identifier
	 * @return {String}
	 */
	_whenThenBinHigh(binIdentifier) {
		var condition = binIdentifier;
		condition+=(" = ");
		condition+=(this.numberOfBins);
		var thenValue;
		if (this.customLabels && this.customLabels[this.numberOfBins] != null) {
			thenValue = ExpressionUtil.getLiteral(this.customLabels[this.numberOfBins]);
		} else if (this._isCustomRanges()) {
			thenValue = this._thenBinMiddle(this.customRanges[this.numberOfBins - 1], this.customRanges[this.numberOfBins]);
		} else {
			var binHigh = MacroUtil.injectMacro(this._binHighCalc());
			thenValue = MacroUtil.substitute(ExpressionUtil.getLiteral("%1"), ExpressionUtil.getLiteral(binHigh), BinExpression._binHigh(this.expressionLocale), this.expressionLocale);
			thenValue = MacroUtil.macroAsString(thenValue);
			thenValue = MacroUtil.macro(thenValue);
		}
		return ExpressionUtil.whenThen(condition, thenValue);
	}

	/**
	 * Calculation that generates the number to put in the binHigh label
	 * @return {String}
	 */
	_binHighCalc() {
		if (HighLevelDataType.INTEGER === this.queryItem.getHighlevelDatatype()) {
			return this._binHighCalcInteger();
		}
		var binHigh = this.minForReport;
		binHigh+=(" + ");
		binHigh+=(this.numberOfBins - 1);
		binHigh+=(" * ");
		binHigh+=(this.binWidth);
		return this._castToDecimalThenVarchar$0(binHigh);
	}

	/**
	 * Calculation that generates the number to put in the binHigh label for an INTEGER
	 * @return {String}
	 */
	_binHighCalcInteger() {
		var binHighInteger = this.virtualMaxValue;
		binHighInteger+=(" - 1");
		var binHigh = this.minForReport;
		binHigh+=(" + ");
		binHigh+=(this.numberOfBins - 1);
		binHigh+=(" * ");
		binHigh+=(this.binWidth);
		return this._castToDecimalThenVarchar$0(ExpressionUtil.inlineIfThenElse(this.integerCheck, binHighInteger, binHigh));
	}

	/**
	 * Creates the when() expression for case when minimum and maximum are the same value. Will generate (pseudo-syntax): - when ( binNumberIdentifier == -1 ) then ( cast ( cast ( id, decimal ) VARCHAR_20 ) )
	 * @param {String} binIdentifier  - The bin number QueryItem identifier
	 * @return {String}
	 */
	_whenThenBinEdge(binIdentifier) {
		var condition = binIdentifier;
		condition+=(" = -1");
		return ExpressionUtil.whenThen(condition, this._castToDecimalThenVarchar$1(this.identifier, false));
	}

	/**
	 * Cast an expression to decimal then varchar, if the QueryItem is an integer ceil() is also applied
	 * @param {String} expr  - The expression to wrap
	 * @return {String}
	 */
	_castToDecimalThenVarchar$0(expr) {
		return this._castToDecimalThenVarchar$1(expr, true);
	}

	/**
	 * Cast an expression to decimal then varchar, if the QueryItem is an integer ceil() is also applied
	 * @param {String} expr  - The expression to wrap
	 * @return {String}
	 */
	_castToDecimalThenVarchar$1(expr, ceil) {
		var result = expr;
		if (ceil && HighLevelDataType.INTEGER === this.queryItem.getHighlevelDatatype()) {
			result = ExpressionUtil.ceil(result);
		}
		var decimalPlaces = HighLevelDataType.INTEGER === this.queryItem.getHighlevelDatatype() ? 0 : this.maxDecimalPlaces;
		var decimal = "decimal(18" + FormatSymbols.getListSeparator(this.expressionLocale) + " " + decimalPlaces + ")";
		return ExpressionUtil.cast(ExpressionUtil.cast(result, decimal, this.expressionLocale), "varchar(20)", this.expressionLocale);
	}

	/**
	 * Return a list of numbers to be used as the bin ranges of the MUI wizard. Values are calculated based on min and max values found in the data, and whether the QueryItem being binned has its HighLevelDataType as INTEGER. When custom ranges are specified, the upper and lower bounds are calculated based on steps between the bin ranges unless they were specified as well. Examples: (Integer) [1, 15, 30, 44, 58, 72, 86, 100] (Decimal) [1.000, 15.143, 29.286, 43.429, 57.571, 71.714, 85.857, 100.000]
	 * @return {Array}  A list of (numberOfBins + 1) strings representing numbers to display
	 */
	getRanges() {
		var isInteger = HighLevelDataType.INTEGER === this.queryItem.getHighlevelDatatype();
		if (this._isCustomRanges()) {
			return this._getCustomRanges(isInteger);
		}
		var ranges = [];
		for (var __i_enFor0 = 0, __exp_enFor0 = isInteger ? this._rawIntegerBinLimits() : this._rawBinLimits(), __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var value = __exp_enFor0[__i_enFor0];
			ranges.push(BinExpressionUtil.formatDouble(value, isInteger ? 0 : this.maxDecimalPlaces));
		}
		return ranges;
	}

	/**
	 * Return ranges when custom ranges are specified, the upper and lower bounds are calculated based on steps between the bin ranges unless they were specified as well. Return a list of number
	 * @param {boolean} isInteger
	 * @return {Array}
	 */
	_getCustomRanges(isInteger) {
		var ranges = [];
		Array.prototype.push.apply(ranges, this.customRanges);
		if (ranges[0] == null) {
			ranges[0] = this._getCustomLowRange(isInteger);
		}
		if (ranges[this.numberOfBins] == null) {
			ranges[this.numberOfBins] = this._getCustomHighRange(isInteger);
		}
		return ranges;
	}

	/**
	 * Get a custom range for the lowest number, this means the user did not specify a low bound. This generated number will be the default shown by MUI if the user ever clicks on the low bound. The logic is: - If next range is greater than the data's min value, return the min value - If next range is less than the data's min value, return the next range minus the delta to the next value
	 * @param {boolean} isInteger
	 * @return {String}
	 */
	_getCustomLowRange(isInteger) {
		var highValue = this.customRanges[2];
		var low = parseFloat(this.customRanges[1]);
		var high = highValue == null ? this.maxValue : parseFloat(highValue);
		var newMin = low > this.minValue ? this.minValue : low - high + low;
		return BinExpressionUtil.formatDouble(newMin, isInteger ? 0 : this.maxDecimalPlaces);
	}

	/**
	 * Get a custom range for the highest number, this means the user did not specify a high bound. This generated number will be the default shown by MUI if the user ever clicks on the high bound. The logic is: - If previous range was less than the data's max value, return the max value - If previous range was greater than the data's max value, return the previous range plus the delta to the previous value
	 * @param {boolean} isInteger
	 * @return {String}
	 */
	_getCustomHighRange(isInteger) {
		var lowValue = this.customRanges[this.numberOfBins - 2];
		var low = lowValue == null ? this.minValue : parseFloat(lowValue);
		var high = parseFloat(this.customRanges[this.numberOfBins - 1]);
		var newHigh = high < this.maxValue ? this.maxValue : high + high - low;
		return BinExpressionUtil.formatDouble(newHigh, isInteger ? 0 : this.maxDecimalPlaces);
	}

	/**
	 * Return a list of labels to be used as the bin labels of the MUI wizard. Values are calculated based on whether custom labels are provided, min and max values found in the data, and whether the QueryItem being binned has HighLevelDataType = INTEGER. Examples: (Custom ranges unbound) [Null, less than 30, 30 to 45, 45 to 60, 60 to 75, 75 and above] (Custom ranges bound) [Null, 15 to 30, 30 to 45, 45 to 60, 60 to 75, 75 to 90] (Equal distribution bins, integer) [Null, less than 21, 21 to 41, 41 to 61, 61 to 81, 81 and above] (Equal distribution bins, decimal) [Null, less than 20.8000, 20.8000 to 40.6000, 40.6000 to 60.4000, 60.4000 to 80.2000, 80.2000 and above]
	 * @return {Array}  A list of numberOfBins strings representing numbers to display
	 */
	getLabels() {
		var isInteger = HighLevelDataType.INTEGER === this.queryItem.getHighlevelDatatype();
		var labels = [];
		labels.push(this._createBinNullLabel());
		if (!(this._isCustomRanges()) && this.minValue == this.maxValue) {
			var label = BinExpressionUtil.formatDouble(this.minValue, isInteger ? 0 : this.maxDecimalPlaces);
			for (var i = 1; i <= this.numberOfBins; ++i) {
				labels.push(this.customLabels && this.customLabels[i] != null ? this.customLabels[i] : label);
			}
		} else {
			var ranges = this.getRanges();
			for (var i = 1; i <= this.numberOfBins; ++i) {
				if (i == 1) {
					labels.push(this._createBinLowLabel(ranges));
				} else if (i == this.numberOfBins) {
					labels.push(this._createBinHighLabel(ranges));
				} else {
					labels.push(this._createBinMiddleLabel(ranges, i));
				}
			}
		}
		return labels;
	}

	/**
	 * Create the null bin label, default is: Null
	 * @return {String}
	 */
	_createBinNullLabel() {
		return this.customLabels && this.customLabels[0] != null ? this.customLabels[0] : this.defaultLabels[0];
	}

	/**
	 * Create the low bin label, default is: less than 2.5
	 * @param {Array} ranges  - All value ranges
	 */
	_createBinLowLabel(ranges) {
		if (this.customLabels && this.customLabels[1] != null) {
			return this.customLabels[1];
		}
		if (this.customRanges && this.customRanges[0] != null) {
			return this._createBinMiddleLabel(ranges, 1);
		}
		var label = this.defaultLabels[1];
		return label.replace("%1", ranges[1]);
	}

	/**
	 * Create the high bin label, default is: 7.5 and above
	 * @param {Array} ranges  - All value ranges
	 */
	_createBinHighLabel(ranges) {
		if (this.customLabels && this.customLabels[this.numberOfBins] != null) {
			return this.customLabels[this.numberOfBins];
		}
		if (this.customRanges && this.customRanges[this.numberOfBins] != null) {
			return this._createBinMiddleLabel(ranges, this.numberOfBins);
		}
		var label = this.defaultLabels[3];
		return label.replace("%1", ranges[this.numberOfBins - 1]);
	}

	/**
	 * Create the middle bin label, default is: 2.5 to 7.5
	 * @param {Array} ranges  - All value ranges
	 * @param {int} binNumber  - The bin number
	 */
	_createBinMiddleLabel(ranges, binNumber) {
		if (this.customLabels && this.customLabels[binNumber] != null) {
			return this.customLabels[binNumber];
		}
		var label = this.defaultLabels[2];
		label = label.replace("%1", ranges[binNumber - 1]);
		return label.replace("%2", ranges[binNumber]);
	}

	/**
	 * Return a list of four XQE resource names to pass to Query-Service in order to get the translated strings to display in the MUI wizard. The resources in order of : Null, Low, Middle, High Example: [XQE_BIN_binNull, XQE_BIN_binLow, XQE_BIN_binMiddle, XQE_BIN_binHigh]
	 * @return {Array}  A list of four XQE resources
	 */
	static getResources() {
		var resources = [];
		resources.push(BinExpression._resourceBinNull());
		resources.push(BinExpression._resourceBinLow());
		resources.push(BinExpression._resourceBinMiddle());
		resources.push(BinExpression._resourceBinHigh());
		return resources;
	}

	/**
	 * Create V5 expression that calculates a bin number for a QueryItem with HighLevelDataType = INTEGER. Example: case when ( qiId is null ) then ( null ) when ( minimum ( qiId for report ) = maximum ( qiId for report ) ) then ( -1 ) when ( maximum ( qiId for report ) - minimum ( qiId for report ) < 7 ) then ( case when ( qiId >= maximum ( qiId for report ) + ceil ( ( 7 - maximum ( qiId for report ) + minimum ( qiId for report ) ) / 2 ) ) then ( 7 ) else ( width-bucket ( qiId, minimum ( qiId for report ) - floor ( ( 7 - maximum ( qiId for report ) + minimum ( qiId for report ) ) / 2 ), maximum ( qiId for report ) + ceil ( ( 7 - maximum ( qiId for report ) + minimum ( qiId for report ) ) / 2 ), 7 ) ) end ) when ( qiId >= maximum ( qiId for report ) ) then ( 7 ) else ( width-bucket ( qiId, minimum ( qiId for report ), maximum ( qiId for report ), 7 ) ) end
	 * @return {String}  V5 expression to calculate a bin number
	 */
	_getBinNumberExpressionInteger() {
		var expressions = [];
		expressions[0] = this._whenNull();
		expressions[1] = this._whenMinEqualMax();
		expressions[2] = this._whenIntegerRangeTooSmall();
		expressions[3] = this._whenValueEqualMax();
		expressions[4] = this._elseWidthBucket();
		return ExpressionUtil.assembleCase(expressions);
	}

	/**
	 * Create V5 expression to handle the special case when only null's are in the data, XQE has issues otherwise.
	 * @return {String}
	 */
	_whenNull() {
		return ExpressionUtil.whenThen(ExpressionUtil.isNull(this.identifier), "null");
	}

	/**
	 * Create V5 expression that calculates a bin number when requesting a number of bins that is higher than the integer data range.
	 * @return {String}
	 */
	_whenIntegerRangeTooSmall() {
		return ExpressionUtil.whenThen(this.integerCheck, this._binNumberThen());
	}

	/**
	 * Create the then() V5 expression that calculates a bin number when requesting a number of bins that is higher than the integer data range.
	 * @return {String}  The then() V5 expression when binning an INTEGER QueryItem
	 */
	_binNumberThen() {
		var expressions = [];
		var condition = this.identifier;
		condition+=(" >= ");
		condition+=(this.virtualMaxValue);
		expressions[0] = ExpressionUtil.whenThen(condition, ExpressionUtil.getLiteral(this.numberOfBins));
		expressions[1] = ExpressionUtil.elseExp(BinExpressionUtil.widthBucket(this.identifier, this.virtualMinValue, this.virtualMaxValue, this.numberOfBins, this.expressionLocale));
		return ExpressionUtil.nest(ExpressionUtil.assembleCase(expressions));
	}

	/**
	 * Create V5 expression that calculates a bin number for a QueryItem with HighLevelDataType = DECIMAL. Example: case when ( qsId.qiId is null ) then ( null ) when ( minimum ( qsId.qiId for report ) = maximum ( qsId.qiId for report ) ) then ( -1 ) when ( qsId.qiId >= maximum ( qsId.qiId for report ) ) then ( 7 ) else ( width-bucket ( qsId.qiId, minimum ( qsId.qiId for report ), maximum ( qsId.qiId for report ), 7 ) ) end
	 * @return {String}  V5 expression to calculate a bin number
	 */
	_getBinNumberExpressionDecimal() {
		var expressions = [];
		expressions[0] = this._whenNull();
		expressions[1] = this._whenMinEqualMax();
		expressions[2] = this._whenValueEqualMax();
		expressions[3] = this._elseWidthBucket();
		return ExpressionUtil.assembleCase(expressions);
	}

	/**
	 * Create a when/then V5 expression for edge case when minimum and maximum values are equal.
	 * @return {String}
	 */
	_whenMinEqualMax() {
		var condition = this.minForReport;
		condition+=(" = ");
		condition+=(this.maxForReport);
		return ExpressionUtil.whenThen(condition, ExpressionUtil.getLiteral(-1));
	}

	/**
	 * Create a when/then V5 expression for when the value is the maximum, this value should fall in the last bin.
	 * @return {String}
	 */
	_whenValueEqualMax() {
		var condition = this.identifier;
		condition+=(" >= ");
		condition+=(this.maxForReport);
		return ExpressionUtil.whenThen(condition, ExpressionUtil.getLiteral(this.numberOfBins));
	}

	/**
	 * Create an else V5 expression that uses width bucket to generate the bin numbers.
	 * @return {String}
	 */
	_elseWidthBucket() {
		return ExpressionUtil.elseExp(BinExpressionUtil.widthBucket(this.identifier, this.minForReport, this.maxForReport, this.numberOfBins, this.expressionLocale));
	}

	/**
	 * Create V5 expression that calculates a bin number for a QueryItem when custom ranges are specified. Examples: (No boundaries): case when ( qiId is null ) then ( null ) when ( qiId < 15 ) then ( 1 ) when ( qiId < 30 ) then ( 2 ) when ( qiId < 45 ) then ( 3 ) when ( qiId < 60 ) then ( 4 ) when ( qiId < 75 ) then ( 5 ) when ( qiId < 85 ) then ( 6 ) else ( 7 ) end (Low and High bounds) case when ( qiId is null ) then ( null ) when ( qiId >= 1 and qiId < 15 ) then ( 1 ) when ( qiId < 30 ) then ( 2 ) when ( qiId < 45 ) then ( 3 ) when ( qiId < 60 ) then ( 4 ) when ( qiId < 75 ) then ( 5 ) when ( qiId < 85 ) then ( 6 ) when ( qiId < 100 ) then ( 7 ) else ( -1 ) end (Low bound only) case when ( qiId is null ) then ( null ) when ( qiId >= 1 and qiId < 15 ) then ( 1 ) when ( qiId < 30 ) then ( 2 ) when ( qiId < 45 ) then ( 3 ) when ( qiId < 60 ) then ( 4 ) when ( qiId < 75 ) then ( 5 ) when ( qiId < 85 ) then ( 6 ) when ( qiId >= 85 ) then ( 7 ) else ( -1 ) end (High bound only) case when ( qiId is null ) then ( null ) when ( qiId < 15 ) then ( 1 ) when ( qiId < 30 ) then ( 2 ) when ( qiId < 45 ) then ( 3 ) when ( qiId < 60 ) then ( 4 ) when ( qiId < 75 ) then ( 5 ) when ( qiId < 85 ) then ( 6 ) when ( qiId < 100 ) then ( 7 ) else ( -1 ) end
	 * @return {String}  V5 expression to calculate a bin number
	 */
	_getBinNumberExpressionCustomRanges() {
		var highBound = this.customRanges[this.numberOfBins] != null;
		var expressions = [];
		expressions.push(this._whenNull());
		for (var i = 0; i < this.numberOfBins + 1; ++i) {
			var to = this.customRanges[i];
			if (to == null) {
				continue;
			}
			var binNumber = i == 0 ? -1 : i;
			expressions.push(BinExpression._rangeNumberExpression(this.identifier, to, binNumber));
		}
		if (highBound) {
			expressions.push(ExpressionUtil.elseExp("-1"));
		} else {
			expressions.push(ExpressionUtil.elseExp(ExpressionUtil.getLiteral(this.numberOfBins)));
		}
		return ExpressionUtil.assembleCase(BinExpression._toArray(expressions));
	}

	/**
	 * A JS compatible way to translate a String list into a String array.
	 * @param {Array} value
	 * @return {String[]}
	 */
	static _toArray(value) {
		var size = value.length;
		var result = [];
		for (var i = 0; i < size; ++i) {
			result[i] = value[i];
		}
		return result;
	}

	/**
	 * Create range bin number as a V5 expression. Examples: when ( identifier < to )  then ( binNumber )
	 * @param {String} identifier  - Identifier to build around
	 * @param {String} to  - The to value
	 * @param {int} binNumber  - The bin number for this expression
	 * @return {String}
	 */
	static _rangeNumberExpression(identifier, to, binNumber) {
		var condition = identifier;
		condition+=(" < ");
		condition+=(to);
		return ExpressionUtil.whenThen(condition, ExpressionUtil.getLiteral(binNumber));
	}

	/**
	 * Generates the integer bin limits as double values.
	 * @return {Array}  List of bin limits as double values
	 */
	_rawIntegerBinLimits() {
		var min = Math.round(this.minValue);
		var max = Math.round(this.maxValue);
		if (max - min < this.numberOfBins) {
			min = min - Math.floor((this.numberOfBins - this.maxValue + this.minValue) / 2.0);
			max = max + Math.ceil((this.numberOfBins - this.maxValue + this.minValue) / 2.0);
		}
		var increment = (max - min) / this.numberOfBins;
		var limits = [];
		limits.push(min);
		for (var i = 1; i < this.numberOfBins; ++i) {
			limits.push(Math.ceil(min + i * increment));
		}
		limits.push(max);
		return limits;
	}

	/**
	 * Generates the bin limits.
	 * @return {Array}  List of bin limits as double values
	 */
	_rawBinLimits() {
		var increment = (this.maxValue - this.minValue) / this.numberOfBins;
		var limits = [];
		limits.push(this.minValue);
		for (var i = 1; i < this.numberOfBins; ++i) {
			limits.push(this.minValue + increment * i);
		}
		limits.push(this.maxValue);
		return limits;
	}

	/**
	 * Return the null bin label macro.
	 * @param {String} expressionLocale
	 * @return {String}  Null bin macro
	 */
	static _binNull(expressionLocale) {
		var binNull = MacroUtil.getResourceString(MacroUtil.getLiteral(BinExpression._resourceBinNull()));
		return MacroUtil.substr(binNull, 13, expressionLocale);
	}

	/**
	 * Return the lowest bin label macro.
	 * @param {String} expressionLocale
	 * @return {String}  Low bin macro
	 */
	static _binLow(expressionLocale) {
		var binLow = MacroUtil.getResourceString(MacroUtil.getLiteral(BinExpression._resourceBinLow()));
		return MacroUtil.substr(binLow, 13, expressionLocale);
	}

	/**
	 * Return the middle bin label macro.
	 * @param {String} expressionLocale
	 * @return {String}  Middle bin macro
	 */
	static _binMiddle(expressionLocale) {
		var binMiddle = MacroUtil.getResourceString(MacroUtil.getLiteral(BinExpression._resourceBinMiddle()));
		return MacroUtil.substr(binMiddle, 13, expressionLocale);
	}

	/**
	 * Return the highest bin label macro.
	 * @param {String} expressionLocale
	 * @return {String}  High bin macro
	 */
	static _binHigh(expressionLocale) {
		var binHigh = MacroUtil.getResourceString(MacroUtil.getLiteral(BinExpression._resourceBinHigh()));
		return MacroUtil.substr(binHigh, 13, expressionLocale);
	}

	/**
	 * Return the resource for binNull
	 * @return {String}
	 */
	static _resourceBinNull() {
		return "XQE_BIN_binNull";
	}

	/**
	 * Return the resource for binLow
	 * @return {String}
	 */
	static _resourceBinLow() {
		return "XQE_BIN_binLow";
	}

	/**
	 * Return the resource for binMiddle
	 * @return {String}
	 */
	static _resourceBinMiddle() {
		return "XQE_BIN_binMiddle";
	}

	/**
	 * Return the resource for binHigh
	 * @return {String}
	 */
	static _resourceBinHigh() {
		return "XQE_BIN_binHigh";
	}

	castToDecimalThenVarchar(a0, a1) {
		var args = arguments;
		if (args.length == 1) {
			return this.castToDecimalThenVarchar$0(a0);
		}
		return this.castToDecimalThenVarchar$1(a0, a1);
	}
}


// WEBPACK FOOTER //
// ./src/generated/BinExpression.js